import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useAuth } from "utils/useAuth/useAuth";
import { PortalContext } from "App";
import { checkNonMoneyDataForTable, checkMoneyDataForTable } from "utils/utils";
import useThrottleCallback from "utils/useThrottleCallback/useThrottleCallback";
import RecordDetailHeader from "components/RecordDetailHeader/RecordDetailHeader";
import RecordDetail from "components/RecordDetail/RecordDetail";
import Grid from "components/Grid/Grid";
import Divider from "components/Divider/Divider";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import s from "./MerchantDetail.module.scss";
import { queryActivityDetail, queryMdr } from "./model";

const MerchantDetail = ({ path }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const tabContext = useContext(PortalContext);
  const { currentPath } = tabContext;
  const gridId = "AnalyticsMerchantDetailGrid";
  const [detailListLoading, setDetailListLoading] = useState(true);
  const [detailListData, setDetailListData] = useState({});
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [datasource, setDatasource] = useState({ data: [] });
  const [search, setSearch] = useState(false);
  const [merchantId, setMerchantId] = useState("");
  const stateContext = useContext(PortalContext);
  const { detailPageState } = stateContext;
  const [columnsArray, setColumnsArray] = useState([]);

  const windowResizeHandler = useThrottleCallback(() => {
    setWindowWidth(window.innerWidth);
  }, 20);
  useEffect(() => {
    window.addEventListener("resize", windowResizeHandler);
    return () => {
      removeEventListener("resize", windowResizeHandler);
    };
  }, [windowWidth, currentPath]);

  useEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    if (detailPageState && detailPageState[path]) {
      setMerchantId(detailPageState[path].merchantId);
    } else if (sessionState && sessionState[path]) {
      setMerchantId(sessionState[path].merchantId);
    }
  }, [detailPageState[path]]);

  useEffect(() => {
    if (merchantId) {
      const init = async () => {
        await fetchMdr();
        await initialFetch();
      };
      init();
    }
  }, [merchantId]);

  const fetchMdr = async () => {
    setDetailListLoading(true);
    let id;
    if (!merchantId) {
      const sessionState = JSON.parse(sessionStorage.getItem("state"));
      id =
        sessionState && sessionState[path]
          ? sessionState[path].merchantId
          : null;
    }
    const response = await queryMdr({ merchantId: merchantId || id });
    const result = response?.data;
    if (result?.message === "Unauthorized") {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    } else if (result?.responseCode !== "0000") {
      snackbar(
        t("general.message.search.error") + ": " + result?.message,
        "error",
      );
      setLoading(false);
    } else if (result?.responseCode === "0000") {
      setDetailListData(result.data);
    }
    setDetailListLoading(false);
  };

  const initialFetch = async () => {
    setLoading(true);
    let id;
    if (!merchantId) {
      const sessionState = JSON.parse(sessionStorage.getItem("state"));
      id =
        sessionState && sessionState[path]
          ? sessionState[path].merchantId
          : null;
    }
    const response = await queryActivityDetail({
      merchantId: merchantId || id,
    });
    const result = response?.data;
    if (result?.message === "Unauthorized") {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    } else if (result?.responseCode !== "0000") {
      snackbar(
        t("general.message.search.error") + ": " + result?.message,
        "error",
      );
    } else if (result?.responseCode === "0000") {
      setDatasource(result);
    }
    setSearch(false);
    setLoading(false);
  };

  const handleSearch = async () => {
    setSearching(true);
    const response = await queryActivityDetail({ merchantId });
    const result = response?.data;
    if (result?.message === "Unauthorized") {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    } else if (result?.responseCode !== "0000") {
      snackbar(
        t("general.message.search.error") + ": " + result?.message,
        "error",
      );
    } else if (result?.responseCode === "0000") {
      setDatasource(result);
    }
    setSearch(true);
    setSearching(false);
  };

  const columns = [
    {
      index: 0,
      key: "month",
      title: t("page.analytics.merchant.detail.column.title.month"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 1,
      key: "currency",
      title: t(
        "page.analytics.merchant.detail.column.title.transaction.currency",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 2,
      key: "totalAmount",
      title: t("page.analytics.merchant.detail.column.title.total.amount"),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      index: 3,
      key: "totalServiceFee",
      title: t("page.analytics.merchant.detail.column.title.total.service.fee"),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
  ];

  const detailListColumns = [
    {
      index: 0,
      key: "aipMerchantNo",
      title: t("page.analytics.merchant.detail.column.title.aip.mid"),
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 1,
      key: "bankAccount",
      title: t("page.analytics.merchant.detail.column.title.bank.account"),
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 2,
      key: "settleCycle",
      title: t("page.analytics.merchant.detail.column.title.settlement.cycle"),
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 3,
      key: "email",
      title: t("page.analytics.merchant.detail.column.title.email"),
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 4,
      key: "beneficiary",
      title: t("page.analytics.merchant.detail.column.title.beneficiary"),
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 5,
      key: "created",
      title: t("page.analytics.merchant.detail.column.title.created"),
      render: (data, key) => {
        return moment(data[key]).format("YYYY-MM-DD");
      },
    },
    {
      index: 6,
      key: "updated",
      title: t("page.analytics.merchant.detail.column.title.updated"),
      render: (data, key) => {
        return moment(data[key]).format("YYYY-MM-DD");
      },
    },
    {
      index: 7,
      key: "sales",
      title: t("page.analytics.merchant.detail.column.title.sales"),
      render: (data, key) => {
        if (!data[key]) {
          return "-";
        }
        return <div>{data[key].firstName}</div>;
      },
    },
  ];

  const detailListHeader = () => {
    if (detailListData) {
      return (
        <RecordDetailHeader
          indicatorName={t("page.analytics.merchant.detail.indicator.name")}
          name={detailListData.merchantName}
          indecatorId={t("page.analytics.merchant.detail.indicator.id")}
          id={merchantId}
        />
      );
    }
  };

  const detailList = () => {
    return (
      <div className={s.detailListContainer}>
        <div>
          {detailListData && (
            <Grid gridId={gridId}>
              {detailListColumns.map((item) => {
                return (
                  <React.Fragment key={item.key}>
                    <RecordDetail
                      label={item.title}
                      data={item.render(detailListData, item.key)}
                    />
                  </React.Fragment>
                );
              })}
            </Grid>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {detailListHeader()}
        {!detailListLoading && <>{detailList()}</>}
        <Divider margin={32} />
        <div className={s.descriptionBarContainer}>
          <div className={s.descriptionBar}>
            <span>{t("component.remarks.label")}</span>
            <span>{t("page.analytics.merchant.detail.description")}</span>
          </div>
          <FunctionalButtonBar
            loading={loading}
            refreshBtn
            handleRefresh={() => handleSearch()}
            columnDisplayBtn
            columnDisplayProps={{
              columns,
              columnsArray,
              setColumnsArray,
              rememberPreference:
                "analytics-transaction-activity-merchant-detail",
            }}
          />
        </div>
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              search={search}
            />
          )}

          {searching && <Spin type={"search"} />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(MerchantDetail);
